import { CaseProgressBar } from "./CaseProgressBar";
import { CaseOperationHistory } from "./CaseOperationHistory";
import { CaseTags } from "./CaseTags";
import { CaseStatusSettings } from "./CaseStatusSettings";
import { CaseSourcingPanel } from "./CaseSourcingPanel";
import { CaseQuotePanel } from "./CaseQuotePanel";
import { CaseMailAttachments, MailAttachmentList } from "./CaseMailAttachments";
import type { CaseMailAttachment } from "./api";
import {
  CaseRichTextEditor,
  type CaseRichTextEditorHandle,
} from "./CaseRichTextEditor";
import {
  IconAdjustmentsHorizontal,
  IconAlertCircle,
  IconChevronDown,
  IconRefresh,
  IconTrash,
  IconX,
} from "@tabler/icons-react";
import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FormEvent,
} from "react";
import {
  mailFrameDocument,
  renderLinkedLine,
} from "../triage/OriginalMailBody";
import {
  caseCommand,
  createEmailTemplate,
  deleteEmailTemplate,
  addCaseCollaborationMember,
  appendCaseMessage,
  draftAction,
  loadCaseMessageHistory,
  loadCaseMessages,
  loadCaseMentionUsers,
  loadCaseCollaboration,
  loadCaseFilterOptions,
  loadCasePage,
  loadCaseWorkspace,
  loadEmailTemplates,
  manualOptions,
  automaticOptions,
  outcomeNames,
  progressNames,
  markCaseRead,
  markCaseUnread,
  removeCaseCollaborationMember,
  sendCaseExternalMessage,
  updateEmailTemplate,
  uploadCaseRichTextImage,
  updateCaseReadState,
  type CaseDetail,
  type CaseFilterOptions,
  type CaseExternalMessage,
  type CaseCollaboration,
  type CaseReadState,
  type CasePage,
  type CaseMessage,
  type CaseMentionUser,
  type EmailTemplate,
  type Outcome,
} from "./api";
import { connectCaseRealtime } from "./realtime";
import {
  caseActivityLabel,
  compactRelativeTime,
  displayCaseNumber,
  fullCaseTime,
} from "./case-list-presentation";
import "./cases.css";

const dateLabel = (date: string) => new Date(date).toLocaleString("zh-CN");
type ReplySenderIdentity = NonNullable<CaseDetail["replyContext"]>["sender"] & {
  mailboxId: string;
};

function SenderIdentity({ sender }: { sender: ReplySenderIdentity }) {
  return <>
    {[sender.displayName, sender.occupation].filter(Boolean).join(" - ")}
    {" · "}
    <strong>
      {[sender.mailboxEmail, sender.mailboxRoleType
        ? `${mailboxRoleName(sender.mailboxRoleType)}发件箱` : null]
        .filter(Boolean).join(" · ")}
    </strong>
  </>;
}

function SenderIdentityConfirmation({ sender, onCancel, onConfirm }: {
  sender: ReplySenderIdentity;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const element = dialog.current;
    element?.showModal();
    return () => element?.close();
  }, []);
  return <dialog ref={dialog} className="case-sender-confirm" role="alertdialog"
    aria-labelledby="case-sender-confirm-title" aria-describedby="case-sender-confirm-description"
    onCancel={(event) => { event.preventDefault(); onCancel(); }}>
    <h2 id="case-sender-confirm-title">确认发件身份</h2>
    <p id="case-sender-confirm-description">
      你当前以 [<SenderIdentity sender={sender} />] 的身份发送，确认吗？
    </p>
    <footer>
      <button type="button" autoFocus onClick={onCancel}>取消</button>
      <button type="button" className="case-primary" onClick={onConfirm}>确认发送</button>
    </footer>
  </dialog>;
}

const replySubject = (subject: string, caseNumber: string) => {
  const original = subject.trim();
  if (!original) return `[#${caseNumber}] New messages on this conversation`;
  return /^re\s*:/i.test(original) ? original : `Re: ${original}`;
};
const isOptionalEmail = (value: string) =>
  !value.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
const mailboxRoleNames: Readonly<Record<string, string>> = {
  SALES: "销售",
  INFO: "Info",
  PROCUREMENT: "采购",
  FINANCE: "财务",
  SOURCING: "查货",
};
const mailboxRoleName = (value: string | null) =>
  mailboxRoleNames[value ?? ""] ?? "通用";
type ExistenceFilter = "" | "true" | "false";
type CaseFilterDraft = {
  from: string;
  to: string;
  allHistory: boolean;
  progress: string;
  hasQuote: ExistenceFilter;
  hasSourcing: ExistenceFilter;
  tagIds: string[];
};
const messageAddressHeader = (
  mine: boolean,
  name: string | null | undefined,
  mailbox: string | undefined,
  roleType: string | null | undefined,
  recipients: Array<{ email?: string }> | undefined,
) => {
  const sender = mailbox?.trim() || "未知发件邮箱";
  const typedSender = `${sender}(${roleType ? mailboxRoleName(roleType) : "未分类"})`;
  const recipient =
    recipients
      ?.map((item) => item.email?.trim())
      .filter(Boolean)
      .join(", ") || "收件人未记录";
  return [mine ? "[我]" : null, name?.trim(), `${typedSender} → ${recipient}`]
    .filter(Boolean)
    .join(" · ");
};

type MessageReadPosition = { at: string; messageId: string };
type MessageChannel = "internal" | "external";
const READ_VISIBLE_SETTLE_MS = 300;
const MESSAGE_LIST_BOTTOM_THRESHOLD = 32;
const compareReadPosition = (
  left: MessageReadPosition,
  right: MessageReadPosition,
) =>
  left.at.localeCompare(right.at) ||
  left.messageId.localeCompare(right.messageId);
const isMessageListAtBottom = (element: HTMLElement) =>
  element.scrollHeight - element.scrollTop - element.clientHeight <=
  MESSAGE_LIST_BOTTOM_THRESHOLD;
const isMessageElementVisible = (
  container: HTMLElement,
  element: HTMLElement,
) => {
  const containerRect = container.getBoundingClientRect();
  const messageRect = element.getBoundingClientRect();
  if (containerRect.height === 0 && messageRect.height === 0)
    return isMessageListAtBottom(container);
  const visibleHeight = Math.max(
    0,
    Math.min(messageRect.bottom, containerRect.bottom) -
      Math.max(messageRect.top, containerRect.top),
  );
  return visibleHeight >= Math.min(messageRect.height * 0.5, 120);
};
const uniqueMessages = <T extends { id: string }>(items: T[]) => [
  ...new Map(items.map((item) => [item.id, item])).values(),
];
const draftStatusNames: Record<string, string> = {
  DRAFT: "草稿",
  PENDING_APPROVAL: "待审核",
  APPROVED: "已批准",
  REVOKED: "已撤回",
  SENDING: "正在发送",
  SENT: "发送成功",
  FAILED: "发送失败",
  UNKNOWN: "发送状态待确认",
};
const isReauthenticationFailure = (draft: CaseDetail["drafts"][number]) =>
  draft.status === "FAILED" &&
  draft.latestSendStatus === "FAILED" &&
  (draft.sendErrorCategory === "gmail_reauthentication_required" ||
    draft.sendErrorCategory === "GmailReauthenticationRequiredError");
const isDefiniteSendFailure = (draft: CaseDetail["drafts"][number]) =>
  draft.status === "FAILED" &&
  draft.latestSendStatus === "FAILED" &&
  (isReauthenticationFailure(draft) ||
    draft.sendErrorCategory === "mail_attachment_unavailable" ||
    draft.sendErrorCategory === "cross_mailbox_thread_mismatch" ||
    Boolean(draft.sendErrorCategory?.startsWith("gmail_provider_http_")));
const canRetryDefiniteSendFailure = (draft: CaseDetail["drafts"][number]) =>
  isDefiniteSendFailure(draft) &&
  draft.senderMailboxAuthorizationStatus === "AUTHORIZED" &&
  Boolean(draft.latestSendId);
const draftFailureReason = (draft: CaseDetail["drafts"][number]) => {
  if (draft.sendErrorCategory === "mail_attachment_unavailable") return "附件读取或校验失败，邮件尚未发出，请重试或联系管理员";
  if (isReauthenticationFailure(draft)) {
    const mailbox = draft.senderMailboxEmail?.trim() || "当前发件邮箱";
    if (draft.senderMailboxAuthorizationStatus === "AUTHORIZED") {
      return `邮箱 ${mailbox} 发送时 Gmail 授权已失效；现已重新授权，可以重新发送`;
    }
    return `邮箱 ${mailbox} 的 Gmail 授权已失效，请先重新授权`;
  }
  if (draft.sendErrorCategory?.startsWith("gmail_provider_http_")) {
    const status = draft.sendErrorCategory.slice("gmail_provider_http_".length);
    return `Gmail 拒绝了发送请求（HTTP ${status}），邮件未发送`;
  }
  if (draft.sendErrorCategory === "cross_mailbox_thread_mismatch") {
    return "切换发件邮箱时错误复用了原邮箱的会话标识，邮件未发送";
  }
  if (draft.sendErrorCategory)
    return `发送失败，错误类别：${draft.sendErrorCategory}`;
  return "邮件发送失败";
};
function MessageAuthor({
  name,
  avatarUrl,
}: {
  name: string;
  avatarUrl?: string | null;
}) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const initials =
    Array.from(name.trim()).slice(0, 2).join("").toLocaleUpperCase() || "?";
  return (
    <div className="case-message-author">
      {avatarUrl && avatarUrl !== failedUrl ? (
        <img
          className="case-message-avatar"
          src={avatarUrl}
          alt={`${name}的头像`}
          referrerPolicy="no-referrer"
          onError={() => setFailedUrl(avatarUrl)}
        />
      ) : (
        <span
          className="case-message-avatar"
          role="img"
          aria-label={`${name}的头像`}
        >
          {initials}
        </span>
      )}
      <strong>{name}</strong>
    </div>
  );
}
function MessageBody({
  html,
  text,
  emphasizeQuotedHistory = false,
  transparentBackground = false,
  onLayout,
}: {
  html?: string | null;
  text?: string | null;
  emphasizeQuotedHistory?: boolean;
  transparentBackground?: boolean;
  onLayout?: () => void;
}) {
  const [quotedHistoryOpen, setQuotedHistoryOpen] = useState(false);
  const plainText = text ?? "";
  const lines = plainText.replace(/\r\n?/g, "\n").split("\n");
  const quotedAt = emphasizeQuotedHistory
    ? lines.findIndex((line) =>
        /^(?:On\s.+wrote:|在?\s*.+写道[:：]|.+\s于.+写道[:：]|-{2,}\s*(?:Original Message|原始邮件|转发邮件|Forwarded message)\s*-{2,}|>)/i.test(
          line.trim(),
        ),
      )
    : -1;
  const richParts = quotedAt > 0 && html ? splitQuotedRichBody(html) : null;
  if (quotedAt > 0) {
    const currentReply = lines.slice(0, quotedAt).join("\n").trim();
    const quotedHistory = lines.slice(quotedAt).join("\n").trim();
    return (
      <div className="case-plain-message">
        {richParts?.currentHtml ? (
          <RichMessageFrame
            html={richParts.currentHtml}
            transparentBackground={transparentBackground}
            onLayout={onLayout}
          />
        ) : (
          <PlainMessageLines text={currentReply} keyPrefix="current" />
        )}
        <details
          className="case-quoted-history"
          open={quotedHistoryOpen}
          onToggle={(event) => setQuotedHistoryOpen(event.currentTarget.open)}
        >
          <summary>
            <span className="case-quote-show">显示引用文字</span>
            <span className="case-quote-hide">隐藏引用文字</span>
            <IconChevronDown
              className="case-quote-chevron"
              aria-hidden="true"
            />
          </summary>
          <div
            className={`case-quoted-content${richParts?.quotedHtml ? " rich" : ""}`}
          >
            {richParts?.quotedHtml ? (
              <RichMessageFrame
                html={richParts.quotedHtml}
                emphasizeQuotedHistory
                transparentBackground={transparentBackground}
                visible={quotedHistoryOpen}
              />
            ) : (
              <PlainMessageLines text={quotedHistory} keyPrefix="quoted" />
            )}
          </div>
        </details>
      </div>
    );
  }
  if (html)
    return (
      <RichMessageFrame
        html={html}
        emphasizeQuotedHistory={emphasizeQuotedHistory}
        transparentBackground={transparentBackground}
        onLayout={onLayout}
      />
    );
  return <p className="case-plain-message">{plainText}</p>;
}

function RichMessageFrame({
  html,
  emphasizeQuotedHistory = false,
  transparentBackground = false,
  visible = true,
  onLayout,
}: {
  html: string;
  emphasizeQuotedHistory?: boolean;
  transparentBackground?: boolean;
  visible?: boolean;
  onLayout?: () => void;
}) {
  const [frameHeight, setFrameHeight] = useState(1);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const measure = useCallback(() => {
    const frameDocument = frameRef.current?.contentDocument;
    if (!frameDocument) return;
    const contentHeight = Math.max(
      frameDocument.body.scrollHeight,
      frameDocument.documentElement.scrollHeight,
    );
    frameDocument.documentElement.style.overflowY =
      contentHeight > 320 ? "auto" : "hidden";
    setFrameHeight(Math.min(320, Math.max(1, contentHeight + 2)));
    requestAnimationFrame(() => onLayout?.());
  }, [onLayout]);
  useEffect(() => setFrameHeight(1), [html]);
  useLayoutEffect(() => {
    if (!visible) return;
    const frame = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(frame);
  }, [html, measure, visible]);
  return (
    <iframe
      ref={frameRef}
      title="富文本消息"
      sandbox="allow-same-origin"
      referrerPolicy="no-referrer"
      srcDoc={mailFrameDocument(
        html,
        true,
        emphasizeQuotedHistory,
        transparentBackground,
      )}
      className="case-message-frame"
      style={{ height: `${frameHeight}px` }}
      onLoad={measure}
    />
  );
}

function splitQuotedRichBody(
  html: string,
): { currentHtml: string; quotedHtml: string } | null {
  const document_ = new DOMParser().parseFromString(html, "text/html");
  const firstQuote = [...document_.body.querySelectorAll("blockquote")].find(
    (node) => !node.parentElement?.closest("blockquote"),
  );
  if (!firstQuote) return null;
  let quoteRoot: Node = firstQuote;
  while (quoteRoot.parentNode && quoteRoot.parentNode !== document_.body) {
    quoteRoot = quoteRoot.parentNode;
  }
  const nodes = [...document_.body.childNodes];
  const quoteIndex = nodes.indexOf(quoteRoot as ChildNode);
  if (quoteIndex < 0) return null;
  let quoteStart = quoteIndex;
  const previous = nodes[quoteIndex - 1];
  if (previous instanceof HTMLElement && previous.tagName !== "BR") {
    if (isQuotedReplyMarker(previous.textContent ?? "")) quoteStart -= 1;
  } else if (previous instanceof HTMLBRElement) {
    for (let index = quoteIndex - 2; index >= 0; index -= 1) {
      if (!(nodes[index] instanceof HTMLBRElement)) continue;
      const marker = nodes
        .slice(index + 1, quoteIndex - 1)
        .map((node) => node.textContent ?? "")
        .join("")
        .trim();
      if (isQuotedReplyMarker(marker)) {
        quoteStart = index + 1;
        break;
      }
    }
  }
  const serialize = (items: Node[]) => {
    const holder = document_.createElement("div");
    for (const item of items) holder.append(item.cloneNode(true));
    return holder.innerHTML;
  };
  const currentHtml = serialize(nodes.slice(0, quoteStart)).trim();
  const quotedHtml = serialize(nodes.slice(quoteStart)).trim();
  return currentHtml && quotedHtml ? { currentHtml, quotedHtml } : null;
}

function isQuotedReplyMarker(value: string) {
  return /^(?:On\s.+wrote:|在?\s*.+写道[:：]|.+\s于.+写道[:：])$/i.test(
    value.trim(),
  );
}

function PlainMessageLines({
  text,
  keyPrefix,
}: {
  text: string;
  keyPrefix: string;
}) {
  return (
    <p>
      {text.split("\n").map((line, index) => (
        <Fragment key={`${keyPrefix}-${index}`}>
          {index > 0 ? <br /> : null}
          {renderLinkedLine(line, `${keyPrefix}-${index}`)}
        </Fragment>
      ))}
    </p>
  );
}

export function CaseWorkspace({
  outcome,
  initialId,
}: {
  outcome: Outcome;
  initialId?: string;
}) {
  const [query, setQuery] = useState(""),
    [from, setFrom] = useState(""),
    [to, setTo] = useState(""),
    [allHistory, setAllHistory] = useState(false),
    [progressFilter, setProgressFilter] = useState(""),
    [quoteFilter, setQuoteFilter] = useState<ExistenceFilter>(""),
    [sourcingFilter, setSourcingFilter] = useState<ExistenceFilter>(""),
    [tagFilters, setTagFilters] = useState<string[]>([]),
    [page, setPage] = useState(1);
  const [filterOptions, setFilterOptions] = useState<CaseFilterOptions | null>(
    null,
  );
  const [filterDraft, setFilterDraft] = useState<CaseFilterDraft>({
    from: "",
    to: "",
    allHistory: false,
    progress: "",
    hasQuote: "",
    hasSourcing: "",
    tagIds: [],
  });
  const filterDialog = useRef<HTMLDialogElement>(null);
  const [result, setResult] = useState<CasePage | null>(null),
    [selected, setSelected] = useState(initialId ?? ""),
    [detail, setDetail] = useState<CaseDetail | null>(null);
  const [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [loading, setLoading] = useState(false),
    [refresh, setRefresh] = useState(0);
  const [relativeNow, setRelativeNow] = useState(() => Date.now());
  const [realtimeStatus, setRealtimeStatus] = useState<
    "connecting" | "connected" | "disconnected" | "revoked"
  >("connecting");
  const cursor = useRef("");
  const unreadChanges = useRef(new Set<string>());
  const pendingReadUpdates = useRef(new Map<string, Promise<void>>());
  const manualUnreadCases = useRef(new Set<string>());
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  useEffect(() => {
    const timer = window.setInterval(() => setRelativeNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  const refreshFilterOptions = useCallback(async (signal?: AbortSignal) => {
    try {
      setFilterOptions(await loadCaseFilterOptions(signal));
    } catch (cause) {
      if (!signal?.aborted)
        setError(cause instanceof Error ? cause.message : String(cause));
    }
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    void refreshFilterOptions(controller.signal);
    return () => controller.abort();
  }, [refreshFilterOptions]);
  const applyReadState = useCallback((caseId: string, state: CaseReadState) => {
    setResult((current) =>
      current
        ? {
            ...current,
            items: current.items.map((item) =>
              item.id === caseId ? { ...item, unread: state.unread } : item,
            ),
          }
        : current,
    );
    setDetail((current) =>
      current?.id === caseId ? { ...current, readState: state } : current,
    );
  }, []);
  const markVisibleRead = useCallback(
    (
      caseId: string,
      channel: "INTERNAL" | "EXTERNAL",
      position: { at: string; messageId: string },
    ) => {
      const prior = pendingReadUpdates.current.get(caseId) ?? Promise.resolve();
      const task = prior
        .catch(() => undefined)
        .then(async () => {
          if (manualUnreadCases.current.has(caseId)) return;
          const state = await updateCaseReadState(caseId, channel, position);
          if (!manualUnreadCases.current.has(caseId))
            applyReadState(caseId, state);
        })
        .finally(() => {
          if (pendingReadUpdates.current.get(caseId) === task)
            pendingReadUpdates.current.delete(caseId);
        });
      pendingReadUpdates.current.set(caseId, task);
      return task;
    },
    [applyReadState],
  );
  const mergeMessage = useCallback((caseId: string, message: CaseMessage) => {
    if (selectedRef.current !== caseId) return;
    setDetail((current) => {
      if (!current || current.id !== caseId) return current;
      const withoutMatch = current.notes.filter(
        (note) =>
          note.id !== message.id &&
          (!message.clientMessageId ||
            note.clientMessageId !== message.clientMessageId),
      );
      return {
        ...current,
        notes: [...withoutMatch, message].sort(
          (a, b) =>
            a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id),
        ),
      };
    });
  }, []);
  const syncMessages = useCallback(
    async (caseId: string) => {
      let nextCursor = cursor.current;
      do {
        const page = await loadCaseMessages(caseId, nextCursor);
        if (selectedRef.current !== caseId) return;
        page.items.forEach((message) => mergeMessage(caseId, message));
        cursor.current = page.cursor;
        nextCursor = page.cursor;
        if (!page.hasMore) return;
      } while (selectedRef.current === caseId);
    },
    [mergeMessage],
  );
  useEffect(() => {
    setPage(1);
    setSelected(initialId ?? "");
    setDetail(null);
    setFrom("");
    setTo("");
    setQuery("");
    setAllHistory(false);
    setProgressFilter("");
    setQuoteFilter("");
    setSourcingFilter("");
    setTagFilters([]);
  }, [outcome, initialId]);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    const timer = setTimeout(() => {
      const params = new URLSearchParams({
        outcome,
        search: query,
        page: String(page),
        allHistory: String(allHistory),
      });
      // The server interprets date-only boundaries in the workspace timezone.
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      if (progressFilter) params.set("progress", progressFilter);
      if (quoteFilter) params.set("hasQuote", quoteFilter);
      if (sourcingFilter) params.set("hasSourcing", sourcingFilter);
      if (tagFilters.length) params.set("tagIds", tagFilters.join(","));
      void loadCasePage(params, controller.signal)
        .then((data) => {
          setResult(data);
        })
        .catch((e) => {
          if (!controller.signal.aborted) setError(String(e.message));
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, 180);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [
    outcome,
    query,
    from,
    to,
    allHistory,
    progressFilter,
    quoteFilter,
    sourcingFilter,
    tagFilters,
    page,
    refresh,
  ]);
  useEffect(() => {
    if (!selected) {
      setDetail(null);
      return;
    }
    const controller = new AbortController();
    setDetail((current) => (current?.id === selected ? current : null));
    void loadCaseWorkspace(selected, controller.signal)
      .then((value) => {
        cursor.current = value.messageCursor ?? "";
        setDetail(value);
        if (value.permissions.internalRead) void syncMessages(value.id);
      })
      .catch((e) => {
        if (!controller.signal.aborted) setError(e.message);
      });
    return () => controller.abort();
  }, [selected, refresh, syncMessages]);
  useEffect(() => {
    if (
      !selected ||
      (!detail?.permissions.internalRead && !detail?.permissions.externalRead)
    ) {
      setRealtimeStatus("disconnected");
      return;
    }
    setRealtimeStatus("connecting");
    const disconnect = connectCaseRealtime(selected, {
      onMessage: ({ caseId, message }) => mergeMessage(caseId, message),
      onExternalStatus: ({ caseId }) => {
        if (selectedRef.current === caseId) setRefresh((value) => value + 1);
      },
      onUnreadChanged: ({ changeId }) => {
        if (unreadChanges.current.has(changeId)) return;
        unreadChanges.current.add(changeId);
        if (unreadChanges.current.size > 200) {
          const oldest = unreadChanges.current.values().next().value;
          if (oldest) unreadChanges.current.delete(oldest);
        }
        setRefresh((value) => value + 1);
      },
      onReconnect: () => {
        setRealtimeStatus("connected");
        if (detail.permissions.internalRead) void syncMessages(selected);
      },
      onDisconnected: () => setRealtimeStatus("disconnected"),
      onAccessRevoked: () => {
        if (selectedRef.current !== selected) return;
        setError("实时消息权限已失效，请重新载入");
        setRealtimeStatus("revoked");
        setDetail(null);
      },
    });
    const onVisibility = () => {
      if (document.visibilityState === "visible") void syncMessages(selected);
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      disconnect();
    };
  }, [
    detail?.permissions.externalRead,
    detail?.permissions.internalRead,
    mergeMessage,
    selected,
    syncMessages,
  ]);
  async function appendMessage(
    clientMessageId: string,
    html: string,
  ): Promise<CaseMessage> {
    if (!detail) throw new Error("案件尚未载入");
    const message = await appendCaseMessage(detail.id, {
      clientMessageId,
      html,
    });
    mergeMessage(detail.id, message);
    return message;
  }
  async function mutate(body: Record<string, unknown>) {
    if (!detail || busy) return false;
    setBusy(true);
    setError("");
    try {
      await caseCommand(
        detail.id,
        { ...body, expectedVersion: detail.rowVersion },
        `case-${crypto.randomUUID()}`,
      );
      setRefresh((v) => v + 1);
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return false;
    } finally {
      setBusy(false);
    }
  }
  async function markUnread(caseId: string) {
    if (busy) return;
    setBusy(true);
    setError("");
    const keepUnread = selectedRef.current === caseId;
    if (keepUnread) manualUnreadCases.current.add(caseId);
    try {
      await pendingReadUpdates.current.get(caseId)?.catch(() => undefined);
      applyReadState(caseId, await markCaseUnread(caseId));
    } catch (e) {
      if (keepUnread) manualUnreadCases.current.delete(caseId);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  async function markRead(caseId: string) {
    if (busy) return;
    setBusy(true);
    setError("");
    manualUnreadCases.current.delete(caseId);
    try {
      await pendingReadUpdates.current.get(caseId)?.catch(() => undefined);
      applyReadState(caseId, await markCaseRead(caseId));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  const activeFilterCount = [
    Boolean(from || to || allHistory),
    Boolean(progressFilter),
    Boolean(quoteFilter),
    Boolean(sourcingFilter),
    Boolean(tagFilters.length),
  ].filter(Boolean).length;
  const openFilters = () => {
    void refreshFilterOptions();
    setFilterDraft({
      from,
      to,
      allHistory,
      progress: progressFilter,
      hasQuote: quoteFilter,
      hasSourcing: sourcingFilter,
      tagIds: tagFilters,
    });
    filterDialog.current?.showModal();
  };
  return (
    <section className="case-workspace" aria-label="案件管理">
      <header className="case-workspace-heading">
        <div>
          <h1>{outcomeNames[outcome]}</h1>
          <span>团队共同处理 · {result?.total ?? 0} 个案件</span>
          {selected &&
            (detail?.permissions.internalRead ||
              detail?.permissions.externalRead) && (
              <small aria-live="polite">
                {realtimeStatus === "connected"
                  ? " · 实时消息已连接"
                  : realtimeStatus === "revoked"
                    ? " · 实时消息权限已失效"
                    : " · 实时消息正在恢复"}
              </small>
            )}
        </div>
        <button onClick={() => setRefresh((v) => v + 1)}>刷新</button>
      </header>
      {error && (
        <div role="alert" className="case-error">
          {error}{" "}
          <button onClick={() => setRefresh((v) => v + 1)}>重新载入</button>
        </div>
      )}
      <div className="case-columns">
        <aside className="case-conversation-list" aria-label="案件会话列表">
          <div className="case-search-row">
            <input
              aria-label="搜索全部历史案件"
              placeholder="搜索编号、主题、客户…"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(1);
              }}
            />
            <button
              type="button"
              className={activeFilterCount ? "is-active" : ""}
              aria-label="打开案件筛选"
              onClick={openFilters}
            >
              <IconAdjustmentsHorizontal size={16} aria-hidden="true" />
              筛选
              {activeFilterCount > 0 && <b>{activeFilterCount}</b>}
            </button>
          </div>
          <dialog
            ref={filterDialog}
            className="case-filter-dialog"
            aria-labelledby="case-filter-title"
            onCancel={() => filterDialog.current?.close()}
          >
            <form
              onSubmit={(event) => {
                event.preventDefault();
                setFrom(filterDraft.from);
                setTo(filterDraft.to);
                setAllHistory(filterDraft.allHistory);
                setProgressFilter(filterDraft.progress);
                setQuoteFilter(filterDraft.hasQuote);
                setSourcingFilter(filterDraft.hasSourcing);
                setTagFilters(filterDraft.tagIds);
                setPage(1);
                filterDialog.current?.close();
              }}
            >
              <header>
                <div>
                  <h2 id="case-filter-title">筛选案件</h2>
                  <small>
                    日期按 {result?.filter.timezone ?? "工作空间时间"} 计算
                  </small>
                </div>
                <button
                  type="button"
                  aria-label="关闭筛选"
                  onClick={() => filterDialog.current?.close()}
                >
                  <IconX size={18} aria-hidden="true" />
                </button>
              </header>
              <main>
                <fieldset>
                  <legend>日期</legend>
                  <div className="case-filter-date-range">
                    <label>
                      开始日期
                      <input
                        type="date"
                        value={filterDraft.from}
                        onChange={(event) =>
                          setFilterDraft((value) => ({
                            ...value,
                            from: event.target.value,
                            allHistory: false,
                          }))
                        }
                      />
                    </label>
                    <label>
                      结束日期
                      <input
                        type="date"
                        min={filterDraft.from}
                        value={filterDraft.to}
                        onChange={(event) =>
                          setFilterDraft((value) => ({
                            ...value,
                            to: event.target.value,
                            allHistory: false,
                          }))
                        }
                      />
                    </label>
                  </div>
                  <label className="case-filter-check">
                    <input
                      type="checkbox"
                      checked={filterDraft.allHistory}
                      onChange={(event) =>
                        setFilterDraft((value) => ({
                          ...value,
                          from: event.target.checked ? "" : value.from,
                          to: event.target.checked ? "" : value.to,
                          allHistory: event.target.checked,
                        }))
                      }
                    />
                    全部历史
                  </label>
                </fieldset>
                {filterOptions?.permissions.metrics && (
                  <label>
                    案件指标
                    <select
                      value={filterDraft.progress}
                      onChange={(event) =>
                        setFilterDraft((value) => ({
                          ...value,
                          progress: event.target.value,
                        }))
                      }
                    >
                      <option value="">全部指标</option>
                      {(
                        filterOptions.metrics ??
                        [...automaticOptions, ...manualOptions].map((key) => ({
                          key,
                          name: progressNames[key],
                        }))
                      ).map(({ key: value, name }) => (
                        <option value={value} key={value}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                {filterOptions?.permissions.quotes && (
                  <label>
                    报价单
                    <select
                      value={filterDraft.hasQuote}
                      onChange={(event) =>
                        setFilterDraft((value) => ({
                          ...value,
                          hasQuote: event.target.value as ExistenceFilter,
                        }))
                      }
                    >
                      <option value="">全部</option>
                      <option value="true">已有报价单</option>
                      <option value="false">未创建报价单</option>
                    </select>
                  </label>
                )}
                {filterOptions?.permissions.sourcing && (
                  <label>
                    查货单
                    <select
                      value={filterDraft.hasSourcing}
                      onChange={(event) =>
                        setFilterDraft((value) => ({
                          ...value,
                          hasSourcing: event.target.value as ExistenceFilter,
                        }))
                      }
                    >
                      <option value="">全部</option>
                      <option value="true">已有查货单</option>
                      <option value="false">未创建查货单</option>
                    </select>
                  </label>
                )}
                {filterOptions?.permissions.tags && (
                  <fieldset>
                    <legend>我的标签</legend>
                    <div className="case-filter-tags">
                      {filterOptions.tags.map((tag) => (
                        <label className="case-filter-check" key={tag.id}>
                          <input
                            type="checkbox"
                            checked={filterDraft.tagIds.includes(tag.id)}
                            onChange={(event) =>
                              setFilterDraft((value) => ({
                                ...value,
                                tagIds: event.target.checked
                                  ? [...value.tagIds, tag.id]
                                  : value.tagIds.filter((id) => id !== tag.id),
                              }))
                            }
                          />
                          {tag.name}
                        </label>
                      ))}
                      {!filterOptions.tags.length && (
                        <small>暂无个人标签</small>
                      )}
                    </div>
                    <small>选择多个标签时，匹配其中任意一个。</small>
                  </fieldset>
                )}
              </main>
              <footer>
                <button
                  type="button"
                  onClick={() =>
                    setFilterDraft({
                      from: "",
                      to: "",
                      allHistory: false,
                      progress: "",
                      hasQuote: "",
                      hasSourcing: "",
                      tagIds: [],
                    })
                  }
                >
                  重置
                </button>
                <span />
                <button
                  type="button"
                  onClick={() => filterDialog.current?.close()}
                >
                  取消
                </button>
                <button type="submit" className="case-filter-apply">
                  应用筛选
                </button>
              </footer>
            </form>
          </dialog>
          <small>
            {from || to
              ? "指定日期范围"
              : query || allHistory || outcome === "PENDING"
                ? "全部历史"
                : "最近 30 天"}{" "}
            · {loading ? "加载中…" : `${result?.total ?? 0} 条`}
          </small>
          <div className="case-list-items">
            {result?.items.map((item) => (
              <div
                className={`case-list-row ${item.unread?.isUnread ? "is-unread" : ""}`}
                key={item.id}
              >
                <button
                  className={`case-list-item ${selected === item.id ? "is-selected" : ""}`}
                  onClick={() => {
                    if (selected !== item.id)
                      manualUnreadCases.current.delete(item.id);
                    setSelected(item.id);
                  }}
                >
                  <span className="case-list-number">
                    <span>{displayCaseNumber(item.caseNumber)}</span>
                    <span className="case-list-number-meta">
                      <span
                        className="case-list-relative-time"
                        title={`首次询单：${fullCaseTime(item.firstInquiryAt, result.filter.timezone ?? "Asia/Shanghai")}\n最近活动：${fullCaseTime(item.latestActivityAt, result.filter.timezone ?? "Asia/Shanghai")}`}
                        aria-label={`首次询单距今 ${compactRelativeTime(item.firstInquiryAt, relativeNow)}，最近活动距今 ${compactRelativeTime(item.latestActivityAt, relativeNow)}`}
                      >
                        {caseActivityLabel(
                          item.firstInquiryAt,
                          item.latestActivityAt,
                          relativeNow,
                        )}
                      </span>
                      {Boolean(item.unread?.count) && (
                        <b
                          className="case-unread-badge"
                          aria-label={`${item.unread?.count} 条未读消息`}
                        >
                          {(item.unread?.count ?? 0) > 99
                            ? "99+"
                            : item.unread?.count}
                        </b>
                      )}
                      {item.unread?.manuallyUnread && !item.unread.count && (
                        <b
                          className="case-manual-unread-dot"
                          aria-label="手动标记未读"
                          title="手动标记未读"
                        />
                      )}
                    </span>
                  </span>
                  <strong>{item.subject}</strong>
                  {item.latestInternalMessage && (
                    <span
                      className="case-list-message-preview"
                      title={item.latestInternalMessage.text}
                    >
                      <b aria-hidden="true">内</b>
                      <span>{item.latestInternalMessage.text}</span>
                    </span>
                  )}
                  {item.latestExternalMessage && (
                    <span
                      className="case-list-message-preview"
                      title={item.latestExternalMessage.subject}
                    >
                      <b aria-hidden="true">外</b>
                      <span>{item.latestExternalMessage.subject}</span>
                    </span>
                  )}
                  {Boolean(item.personalTags?.length) && (
                    <span
                      className="case-list-tags"
                      aria-label={`案件标签：${item.personalTags?.map((tag) => tag.name).join("、")}`}
                    >
                      {item.personalTags?.map((tag) => (
                        <span className="case-list-tag" key={tag.id}>
                          {tag.name}
                        </span>
                      ))}
                    </span>
                  )}
                </button>
                {item.unread && item.unread.availableChannels.length > 0 && (
                  <details className="case-item-menu">
                    <summary aria-label={`打开 ${item.caseNumber} 操作菜单`}>
                      ⋯
                    </summary>
                    <div role="menu" aria-label={`${item.caseNumber} 消息状态`}>
                      <button
                        role="menuitem"
                        disabled={busy || item.unread.isUnread}
                        onClick={(event) => {
                          event.currentTarget
                            .closest("details")
                            ?.removeAttribute("open");
                          void markUnread(item.id);
                        }}
                      >
                        标记未读
                      </button>
                      <button
                        role="menuitem"
                        disabled={busy || !item.unread.isUnread}
                        onClick={(event) => {
                          event.currentTarget
                            .closest("details")
                            ?.removeAttribute("open");
                          void markRead(item.id);
                        }}
                      >
                        标记已读
                      </button>
                    </div>
                  </details>
                )}
              </div>
            ))}
            {!loading && result?.items.length === 0 && (
              <p>当前条件下没有案件</p>
            )}
          </div>
          <nav aria-label="案件分页">
            <button
              disabled={page === 1 || loading}
              onClick={() => setPage((v) => v - 1)}
            >
              上一页
            </button>
            <span>第 {page} 页</span>
            <button
              disabled={!result?.hasNext || loading}
              onClick={() => setPage((v) => v + 1)}
            >
              下一页
            </button>
          </nav>
        </aside>
        {detail &&
        (detail.permissions.internalRead || detail.permissions.externalRead) ? (
          <CaseConversation
            key={detail.id}
            detail={detail}
            busy={busy}
            mutate={mutate}
            appendMessage={appendMessage}
            onVisibleRead={markVisibleRead}
            onRefresh={() => setRefresh((v) => v + 1)}
          />
        ) : (
          <main className="case-empty">
            {detail
              ? "没有案件消息查看权限"
              : selected
                ? "正在加载案件…"
                : "选择一个案件，开始团队协作"}
          </main>
        )}
        {detail && (
          <CaseSidebar
            detail={detail}
            busy={busy}
            mutate={mutate}
            onRefresh={() => {
              void refreshFilterOptions();
              setRefresh((v) => v + 1);
            }}
          />
        )}
      </div>
    </section>
  );
}
type DetailProps = {
  detail: CaseDetail;
  busy: boolean;
  mutate: (body: Record<string, unknown>) => Promise<boolean>;
};
type PendingMessage = {
  clientMessageId: string;
  html: string;
  text: string;
  createdAt: string;
  status: "sending" | "failed";
};

export function signatureHtml(html: string): string {
  // This is a real editable paragraph rather than visual margin. Users can
  // place the caret before a default signature immediately.
  return `<p data-crm-signature-spacer="true"><br></p><div data-crm-signature="true">${html}</div>`;
}

export function withoutSignature(html: string): string {
  const holder = document.createElement("div");
  holder.innerHTML = html;
  holder.querySelectorAll('[data-crm-signature="true"]').forEach((element) => {
    const managedSpacer = element.previousElementSibling;
    if (managedSpacer?.getAttribute("data-crm-signature-spacer") === "true") {
      if (managedSpacer.textContent?.trim())
        managedSpacer.removeAttribute("data-crm-signature-spacer");
      else managedSpacer.remove();
    }
    // Compatibility cleanup for drafts created before the separator was moved
    // into the signature wrapper. Remove every adjacent blank paragraph so
    // repeated on/off clicks cannot accumulate empty lines.
    let previous = element.previousElementSibling;
    while (
      previous?.tagName === "P" &&
      !previous.textContent?.trim() &&
      !previous.querySelector("img, table, hr")
    ) {
      const next = previous.previousElementSibling;
      previous.remove();
      previous = next;
    }
    element.remove();
  });
  return holder.innerHTML;
}

export function withSignature(html: string, signature: string): string {
  const body = withoutSignature(html).trim();
  return `${body}${signatureHtml(signature)}`;
}

function messageTextWithoutSignature(html: string): string {
  const holder = document.createElement("div");
  holder.innerHTML = withoutSignature(html);
  return holder.textContent?.trim() ?? "";
}

function CaseConversation({
  detail,
  busy,
  appendMessage,
  onVisibleRead,
  onRefresh,
}: DetailProps & {
  appendMessage: (
    clientMessageId: string,
    html: string,
  ) => Promise<CaseMessage>;
  onVisibleRead: (
    caseId: string,
    channel: "INTERNAL" | "EXTERNAL",
    position: { at: string; messageId: string },
  ) => Promise<void>;
  onRefresh: () => void;
}) {
  const [channel, setChannel] = useState<"internal" | "external">(
    detail.permissions.internalRead ? "internal" : "external",
  );
  const subject = detail.replyContext?.subject ?? replySubject(
    detail.replyContext?.replyTarget.subject ?? detail.subject,
    detail.caseNumber,
  );
  const [editorVersion, setEditorVersion] = useState(0);
  const [notice, setNotice] = useState("");
  const [sending, setSending] = useState(false);
  const [editorCharacterCount, setEditorCharacterCount] = useState(0);
  const [editorUploading, setEditorUploading] = useState(false);
  const [mailAttachments, setMailAttachments] = useState<CaseMailAttachment[]>([]);
  const [attachmentsBlocked, setAttachmentsBlocked] = useState(false);
  const [attachmentReset, setAttachmentReset] = useState(0);
  const [selectedMailboxId, setSelectedMailboxId] = useState("");
  const [bccEmail, setBccEmail] = useState(
    detail.replyContext?.defaultBccEmail ?? "",
  );
  const [bccEditing, setBccEditing] = useState(false);
  const [signatureEnabled, setSignatureEnabled] = useState(
    detail.replyContext?.signature?.configured ?? false,
  );
  const [pendingMessages, setPendingMessages] = useState<PendingMessage[]>([]);
  const [withdrawnDraftIds, setWithdrawnDraftIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [olderInternal, setOlderInternal] = useState<CaseMessage[]>([]);
  const [olderExternal, setOlderExternal] = useState<CaseExternalMessage[]>([]);
  const [historyState, setHistoryState] = useState(detail.messageHistory);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [mentionUsers, setMentionUsers] = useState<CaseMentionUser[]>([]);
  const [mentionStatus, setMentionStatus] = useState("");
  const [mentionRetry, setMentionRetry] = useState(0);
  useEffect(() => {
    setMentionUsers([]);
    if (!detail.permissions.internalRead || !detail.permissions.internalSend)
      return;
    const controller = new AbortController();
    setMentionStatus("协作成员加载中…");
    void loadCaseMentionUsers(detail.id, controller.signal)
      .then((users) => {
        if (controller.signal.aborted) return;
        setMentionUsers(users);
        setMentionStatus(users.length ? "" : "当前案件没有可 @ 的协作成员");
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted)
          setMentionStatus(
            `协作成员加载失败${error instanceof Error ? `：${error.message}` : "，请重试"}`,
          );
      });
    return () => controller.abort();
  }, [
    detail.id,
    detail.permissions.internalRead,
    detail.permissions.internalSend,
    mentionRetry,
  ]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [confirmingSend, setConfirmingSend] = useState<{
    html: string;
    text: string;
    sender: ReplySenderIdentity;
    requiresSenderConfirmation: boolean;
  } | null>(null);
  const [confirmingSender, setConfirmingSender] = useState(false);
  useEffect(() => {
    setConfirmingSend(null);
    setConfirmingSender(false);
  }, [detail.id]);
  const editor = useRef<CaseRichTextEditorHandle>(null);
  const messageList = useRef<HTMLDivElement>(null);
  const channelScrollState = useRef<
    Record<
      MessageChannel,
      { initialized: boolean; scrollTop: number; stickToBottom: boolean }
    >
  >({
    internal: { initialized: false, scrollTop: 0, stickToBottom: true },
    external: { initialized: false, scrollTop: 0, stickToBottom: true },
  });
  const visibleMessageIds = useRef(new Set<string>());
  const visibleReadTimer = useRef<number | null>(null);
  const lastRequestedRead = useRef<Record<MessageChannel, string | null>>({
    internal: null,
    external: null,
  });
  const [atBottom, setAtBottom] = useState(true);
  useEffect(() => {
    setOlderInternal([]);
    setOlderExternal([]);
    setHistoryState(detail.messageHistory);
    setEditorCharacterCount(0);
    setEditorUploading(false);
    setBccEmail(detail.replyContext?.defaultBccEmail ?? "");
    setBccEditing(false);
    setSignatureEnabled(detail.replyContext?.signature?.configured ?? false);
    setWithdrawnDraftIds(new Set());
  }, [detail.id, detail.replyContext?.defaultBccEmail]);
  useEffect(() => {
    const senders = detail.replyContext?.senders ?? [];
    const preferred =
      senders.find(
        (sender) =>
          sender.canSend &&
          sender.mailboxEmail === detail.replyContext?.sender.mailboxEmail,
      ) ?? senders.find((sender) => sender.canSend);
    setSelectedMailboxId(preferred?.mailboxId ?? "");
  }, [detail.id, detail.replyContext]);
  useEffect(() => {
    if (!detail.permissions.externalSend) return;
    const controller = new AbortController();
    void loadEmailTemplates(controller.signal)
      .then(setTemplates)
      .catch((error) => {
        if (!controller.signal.aborted)
          setNotice(error instanceof Error ? error.message : String(error));
      });
    return () => controller.abort();
  }, [detail.id, detail.permissions.externalSend]);
  async function retrySend(id: string, retryFromSendId?: string | null) {
    if (sending) return;
    setSending(true);
    try {
      const key = `draft-retry-${id}-${retryFromSendId}`;
      await draftAction(detail.id, id, "retry", key);
      setNotice("重新发送请求已进入队列");
      onRefresh();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : String(e));
    } finally {
      setSending(false);
    }
  }

  async function withdrawFailedSend(id: string, subject: string) {
    if (sending) return;
    if (!window.confirm(`确认撤回“${subject}”？撤回后将从当前会话记录中移除。`))
      return;
    setSending(true);
    try {
      await draftAction(
        detail.id,
        id,
        "withdraw",
        `draft-withdraw-${id}-${crypto.randomUUID()}`,
      );
      setWithdrawnDraftIds((current) => new Set([...current, id]));
      setNotice("失败消息已撤回");
      onRefresh();
    } catch (error) {
      setNotice(error instanceof Error ? error.message : String(error));
    } finally {
      setSending(false);
    }
  }

  const allowed =
    channel === "internal"
      ? detail.permissions.internalSend
      : detail.permissions.externalSend;
  const selectedSender = detail.replyContext?.senders.find(
    (sender) => sender.mailboxId === selectedMailboxId,
  );
  const noSenderMailbox =
    channel === "external" && detail.replyContext?.senders.length === 0;
  const composerDisabled = !allowed || busy || sending || noSenderMailbox;
  const externalBodyValid = editorCharacterCount >= 10;
  const configuredSignature = detail.replyContext?.signature?.configured
    ? detail.replyContext.signature.htmlBody
    : "";
  function toggleSignature(enabled: boolean) {
    setSignatureEnabled(enabled);
    const richEditor = editor.current;
    const snapshot = richEditor?.getSnapshot();
    if (!snapshot || !richEditor) return;
    richEditor.setHtml(
      enabled && configuredSignature
        ? withSignature(snapshot.html, configuredSignature)
        : withoutSignature(snapshot.html),
    );
  }
  async function submit() {
    if (channel === "external" && attachmentsBlocked) return;
    const snapshot = editor.current?.getSnapshot() ?? { html: "", text: "" };
    if (!snapshot.text) return;
    if (channel === "internal") {
      const pending: PendingMessage = {
        clientMessageId: crypto.randomUUID(),
        html: snapshot.html,
        text: snapshot.text,
        createdAt: new Date().toISOString(),
        status: "sending",
      };
      setPendingMessages((current) => [...current, pending]);
      await sendInternal(pending, true);
      return;
    }
    if (!detail.replyContext?.replyTarget || !detail.replyContext.recipient) {
      setNotice("该案件没有可回复的客户来信");
      return;
    }
    if (!selectedSender?.canSend) {
      setNotice(selectedSender?.unavailableReason ?? "请选择可用的发件邮箱");
      return;
    }
    if (!externalBodyValid) {
      setNotice("邮件正文至少需要 10 个字符");
      return;
    }
    if (!isOptionalEmail(bccEmail)) {
      setNotice("请输入正确的密送邮箱地址");
      return;
    }
    if (!detail.replyContext.canSend) {
      setNotice(
        detail.replyContext.unavailableReason ?? "当前无法使用该发件箱发送",
      );
      return;
    }
    setConfirmingSend({
      html: snapshot.html,
      text: snapshot.text,
      sender: { ...detail.replyContext.sender, ...selectedSender },
      requiresSenderConfirmation: detail.replyContext.senders.length > 1,
    });
  }
  async function confirmDirectSend(identityConfirmed = false) {
    if (!confirmingSend || !detail.replyContext || sending)
      return;
    if ((confirmingSend.requiresSenderConfirmation || detail.replyContext.senders.length > 1) && !identityConfirmed) {
      setConfirmingSender(true);
      return;
    }
    setConfirmingSender(false);
    setSending(true);
    try {
      await sendCaseExternalMessage(
        detail.id,
        {
          mailboxId: confirmingSend.sender.mailboxId,
          replyToMessageId: detail.replyContext.replyTarget.messageId,
          html: confirmingSend.html,
          bccEmail: bccEmail.trim(),
          // The signature is already part of the editor document. Keeping this
          // false prevents the API from appending a second copy at send time.
          includeSignature: false,
          fileAttachmentIds: mailAttachments.map(file => file.id),
        },
        `case-direct-send-${crypto.randomUUID()}`,
      );
      setConfirmingSend(null);
      setEditorVersion((value) => value + 1);
      setEditorCharacterCount(0);
      setSelectedTemplateId("");
      setNotice("发送请求已进入队列");
      setMailAttachments([]);
      setAttachmentsBlocked(false);
      setAttachmentReset(value => value + 1);
      onRefresh();
    } catch (error) {
      setNotice(error instanceof Error ? error.message : String(error));
    } finally {
      setSending(false);
    }
  }
  function useTemplate(id: string) {
    setSelectedTemplateId(id);
    const template = templates.find((item) => item.id === id);
    if (!template) return;
    editor.current?.setHtml(
      signatureEnabled && configuredSignature
        ? withSignature(template.htmlBody, configuredSignature)
        : template.htmlBody,
    );
    setNotice(`已载入模板“${template.name}”`);
  }
  async function saveTemplate() {
    const snapshot = editor.current?.getSnapshot() ?? { html: "", text: "" };
    if (!snapshot.text) {
      setNotice("请先填写邮件正文");
      return;
    }
    const current = templates.find((item) => item.id === selectedTemplateId);
    const name = window.prompt("模板名称", current?.name ?? "")?.trim();
    if (!name) return;
    setSending(true);
    try {
      const current = templates.find((item) => item.id === selectedTemplateId);
      const sameName = templates.find(
        (item) =>
          item.name.normalize("NFKC").trim().toLocaleLowerCase() ===
          name.normalize("NFKC").trim().toLocaleLowerCase(),
      );
      const target = current ?? sameName;
      const saved = target
        ? await updateEmailTemplate(target.id, {
            name,
            subject: "",
            html: withoutSignature(snapshot.html),
          })
        : await createEmailTemplate({
            name,
            subject: "",
            html: withoutSignature(snapshot.html),
          });
      setTemplates((items) => [
        saved,
        ...items.filter((item) => item.id !== saved.id),
      ]);
      setSelectedTemplateId(saved.id);
      setNotice(target ? "模板已更新" : "模板已保存");
    } catch (error) {
      setNotice(error instanceof Error ? error.message : String(error));
    } finally {
      setSending(false);
    }
  }
  async function removeSelectedTemplate() {
    const template = templates.find((item) => item.id === selectedTemplateId);
    if (!template || !window.confirm(`确认删除模板“${template.name}”？`))
      return;
    setSending(true);
    try {
      await deleteEmailTemplate(template.id);
      setTemplates((items) => items.filter((item) => item.id !== template.id));
      setSelectedTemplateId("");
      setNotice("模板已删除");
    } catch (error) {
      setNotice(error instanceof Error ? error.message : String(error));
    } finally {
      setSending(false);
    }
  }
  async function sendInternal(message: PendingMessage, clearEditor: boolean) {
    if (sending) return;
    setSending(true);
    setPendingMessages((current) =>
      current.map((item) =>
        item.clientMessageId === message.clientMessageId
          ? { ...item, status: "sending" }
          : item,
      ),
    );
    try {
      await appendMessage(message.clientMessageId, message.html);
      setPendingMessages((current) =>
        current.filter(
          (item) => item.clientMessageId !== message.clientMessageId,
        ),
      );
      if (clearEditor) setEditorVersion((value) => value + 1);
      setNotice("对内消息已保存");
    } catch (error) {
      setPendingMessages((current) =>
        current.map((item) =>
          item.clientMessageId === message.clientMessageId
            ? { ...item, status: "failed" }
            : item,
        ),
      );
      setNotice(error instanceof Error ? error.message : String(error));
    } finally {
      setSending(false);
    }
  }
  async function loadEarlier() {
    if (loadingEarlier) return;
    const state =
      channel === "internal" ? historyState.internal : historyState.external;
    if (!state.hasMore) return;
    channelScrollState.current[channel].stickToBottom = false;
    const list = messageList.current;
    const previousHeight = list?.scrollHeight ?? 0;
    const previousTop = list?.scrollTop ?? 0;
    setLoadingEarlier(true);
    try {
      const page = await loadCaseMessageHistory(
        detail.id,
        channel === "internal" ? "INTERNAL" : "EXTERNAL",
        state.beforeCursor,
      );
      if (page.channel === "INTERNAL") {
        setOlderInternal((current) =>
          uniqueMessages([...page.items, ...current]),
        );
        setHistoryState((current) => ({
          ...current,
          internal: {
            beforeCursor: page.beforeCursor,
            hasMore: page.hasMore,
          },
        }));
      } else {
        setOlderExternal((current) =>
          uniqueMessages([...page.items, ...current]),
        );
        setHistoryState((current) => ({
          ...current,
          external: {
            beforeCursor: page.beforeCursor,
            hasMore: page.hasMore,
          },
        }));
      }
      requestAnimationFrame(() => {
        if (!list) return;
        list.scrollTop = previousTop + (list.scrollHeight - previousHeight);
      });
    } catch (error) {
      setNotice(error instanceof Error ? error.message : String(error));
    } finally {
      setLoadingEarlier(false);
    }
  }
  const messages =
    channel === "internal"
      ? [
          ...uniqueMessages([...olderInternal, ...detail.notes]).map((n) => ({
            id: n.id,
            html: n.safeHtml,
            text: n.body,
            time: n.createdAt,
            label: "对内消息",
            direction: null,
            senderPresentation: null,
            sender: undefined,
            recipients: undefined,
            sentByCurrentUser: false,
            authorName:
              n.messageKind === "SYSTEM"
                ? null
                : n.author?.displayName?.trim() || "未知用户",
            avatarUrl: n.author?.avatarUrl,
            system: n.messageKind === "SYSTEM",
            pending: null as PendingMessage | null,
            readPosition: n.readPosition,
            readEligible: true,
          })),
          ...pendingMessages.map((pending) => ({
            id: `pending-${pending.clientMessageId}`,
            html: pending.html,
            text: pending.text,
            time: pending.createdAt,
            label: pending.status === "sending" ? "发送中" : "发送失败",
            direction: null,
            senderPresentation: null,
            sender: undefined,
            recipients: undefined,
            sentByCurrentUser: false,
            authorName: "我",
            avatarUrl: null,
            system: false,
            pending,
            readPosition: undefined,
            readEligible: false,
          })),
        ].sort(
          (a, b) => a.time.localeCompare(b.time) || a.id.localeCompare(b.id),
        )
      : uniqueMessages([
          ...olderExternal,
          ...detail.conversations.flatMap((c) => c.emailMessages),
        ])
          .map((m) => ({
            id: m.id,
            fileAttachments: m.fileAttachments,
            html: m.safeHtmlBody,
            text: m.textBody,
            time:
              (m.direction === "OUTBOUND" ? m.sentAt : m.receivedAt) ??
              m.createdAt,
            label: m.direction === "INBOUND" ? "客户来信" : "对外消息",
            direction: m.direction,
            senderPresentation: m.senderPresentation,
            sender: m.sender,
            recipients: m.recipients,
            sentByCurrentUser:
              m.direction === "OUTBOUND" &&
              Boolean(m.senderPresentation?.sentByCurrentUser),
            authorName: null,
            avatarUrl: null,
            system: false,
            pending: null as PendingMessage | null,
            readPosition: m.readPosition,
            readEligible:
              m.direction === "INBOUND" ||
              !m.senderPresentation?.sentByCurrentUser,
          }))
          .sort(
            (a, b) => a.time.localeCompare(b.time) || a.id.localeCompare(b.id),
          );
  const currentReadState =
    channel === "internal"
      ? detail.readState?.internal
      : detail.readState?.external;
  const currentUnreadCount =
    channel === "internal"
      ? (detail.readState?.unread.internalCount ?? 0)
      : (detail.readState?.unread.externalCount ?? 0);
  const firstUnreadMessageId = currentReadState?.firstUnreadMessageId ?? null;
  const readThrough = currentReadState?.readThrough ?? null;
  const timelineKey = messages
    .map((message) => `${message.id}:${message.readPosition?.at ?? ""}`)
    .join("|");
  const markVisibleMessagesRead = useCallback(() => {
    if (document.visibilityState !== "visible" || currentUnreadCount < 1)
      return;
    const list = messageList.current;
    if (!list) return;
    const observedByBrowser =
      typeof window.IntersectionObserver !== "undefined";
    let latest: MessageReadPosition | null = null;
    list
      .querySelectorAll<HTMLElement>("[data-read-message-id][data-read-at]")
      .forEach((element) => {
        const messageId = element.dataset.readMessageId;
        const at = element.dataset.readAt;
        if (!messageId || !at) return;
        const visible = observedByBrowser
          ? visibleMessageIds.current.has(messageId)
          : isMessageElementVisible(list, element);
        if (!visible) return;
        const position = { at, messageId };
        if (readThrough && compareReadPosition(position, readThrough) <= 0)
          return;
        if (!latest || compareReadPosition(position, latest) > 0)
          latest = position;
      });
    if (!latest) return;
    const position: MessageReadPosition = latest;
    const requestKey = `${position.at}:${position.messageId}`;
    if (lastRequestedRead.current[channel] === requestKey) return;
    lastRequestedRead.current[channel] = requestKey;
    void onVisibleRead(
      detail.id,
      channel === "internal" ? "INTERNAL" : "EXTERNAL",
      position,
    ).catch(() => {
      if (lastRequestedRead.current[channel] === requestKey)
        lastRequestedRead.current[channel] = null;
    });
  }, [channel, currentUnreadCount, detail.id, onVisibleRead, readThrough]);
  const scheduleVisibleRead = useCallback(() => {
    if (visibleReadTimer.current !== null)
      window.clearTimeout(visibleReadTimer.current);
    visibleReadTimer.current = window.setTimeout(() => {
      visibleReadTimer.current = null;
      markVisibleMessagesRead();
    }, READ_VISIBLE_SETTLE_MS);
  }, [markVisibleMessagesRead]);
  const scrollToLatest = useCallback(() => {
    const list = messageList.current;
    if (!list) return;
    list.scrollTop = list.scrollHeight;
    const state = channelScrollState.current[channel];
    state.scrollTop = list.scrollTop;
    state.stickToBottom = true;
    setAtBottom(true);
    scheduleVisibleRead();
  }, [channel, scheduleVisibleRead]);
  const keepOpenedViewAtLatest = useCallback(() => {
    if (channelScrollState.current[channel].stickToBottom) {
      requestAnimationFrame(scrollToLatest);
    } else {
      scheduleVisibleRead();
    }
  }, [channel, scheduleVisibleRead, scrollToLatest]);
  const handleMessageScroll = useCallback(() => {
    const list = messageList.current;
    if (!list) return;
    const bottom = isMessageListAtBottom(list);
    const state = channelScrollState.current[channel];
    state.scrollTop = list.scrollTop;
    state.stickToBottom = bottom;
    setAtBottom(bottom);
    scheduleVisibleRead();
  }, [channel, scheduleVisibleRead]);
  useLayoutEffect(() => {
    const list = messageList.current;
    if (!list) return;
    const state = channelScrollState.current[channel];
    if (state.initialized) {
      if (state.stickToBottom) list.scrollTop = list.scrollHeight;
      else list.scrollTop = state.scrollTop;
    } else {
      state.initialized = true;
      const firstUnread = firstUnreadMessageId
        ? [...list.querySelectorAll<HTMLElement>("[data-message-id]")].find(
            (element) => element.dataset.messageId === firstUnreadMessageId,
          )
        : undefined;
      if (firstUnread) {
        const listRect = list.getBoundingClientRect();
        const messageRect = firstUnread.getBoundingClientRect();
        list.scrollTop = Math.max(
          0,
          list.scrollTop + messageRect.top - listRect.top - 8,
        );
      } else {
        list.scrollTop = list.scrollHeight;
      }
      state.stickToBottom = isMessageListAtBottom(list);
      state.scrollTop = list.scrollTop;
    }
    setAtBottom(state.stickToBottom);
    scheduleVisibleRead();
  }, [channel, detail.id, firstUnreadMessageId, scheduleVisibleRead]);
  useLayoutEffect(() => {
    const state = channelScrollState.current[channel];
    if (!state.initialized) return;
    if (state.stickToBottom) scrollToLatest();
    else scheduleVisibleRead();
  }, [
    channel,
    currentUnreadCount,
    scrollToLatest,
    scheduleVisibleRead,
    timelineKey,
  ]);
  useEffect(() => {
    if (currentUnreadCount === 0) lastRequestedRead.current[channel] = null;
  }, [channel, currentUnreadCount]);
  useEffect(() => {
    const list = messageList.current;
    if (!list) return;
    visibleMessageIds.current.clear();
    const elements = list.querySelectorAll<HTMLElement>(
      "[data-read-message-id][data-read-at]",
    );
    if (typeof window.IntersectionObserver === "undefined") {
      scheduleVisibleRead();
      return () => {
        if (visibleReadTimer.current !== null)
          window.clearTimeout(visibleReadTimer.current);
      };
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const element = entry.target as HTMLElement;
          const messageId = element.dataset.readMessageId;
          if (!messageId) return;
          const visibleHeight = entry.intersectionRect.height;
          const requiredHeight = Math.min(
            entry.boundingClientRect.height * 0.5,
            120,
          );
          if (entry.isIntersecting && visibleHeight >= requiredHeight)
            visibleMessageIds.current.add(messageId);
          else visibleMessageIds.current.delete(messageId);
        });
        scheduleVisibleRead();
      },
      { root: list, threshold: [0, 0.25, 0.5, 1] },
    );
    elements.forEach((element) => observer.observe(element));
    return () => {
      observer.disconnect();
      visibleMessageIds.current.clear();
      if (visibleReadTimer.current !== null)
        window.clearTimeout(visibleReadTimer.current);
    };
  }, [channel, detail.id, scheduleVisibleRead, timelineKey]);
  useEffect(() => {
    const list = messageList.current;
    if (!list || typeof window.ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      if (channelScrollState.current[channel].stickToBottom) scrollToLatest();
      else scheduleVisibleRead();
    });
    list
      .querySelectorAll<HTMLElement>(".case-message")
      .forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [channel, scheduleVisibleRead, scrollToLatest, timelineKey]);
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (channelScrollState.current[channel].stickToBottom) scrollToLatest();
      else scheduleVisibleRead();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [channel, scheduleVisibleRead, scrollToLatest]);
  return (
    <main className="case-conversation">
      <header>
        <div>
          <strong>{detail.subject}</strong>
          <small>
            {displayCaseNumber(detail.caseNumber)} ·{" "}
            {outcomeNames[detail.businessOutcome]} · v{detail.rowVersion}
          </small>
        </div>
        {detail.permissions.metricsRead && <CaseProgressBar detail={detail} />}
      </header>
      <div className="case-message-tabs" role="tablist">
        {detail.permissions.internalRead && (
          <button
            role="tab"
            aria-selected={channel === "internal"}
            onClick={() => setChannel("internal")}
          >
            对内消息
            {Boolean(detail.readState?.unread.internalCount) && (
              <b className="case-tab-unread">
                {detail.readState?.unread.internalCount}
              </b>
            )}
          </button>
        )}
        {detail.permissions.externalRead && (
          <button
            role="tab"
            aria-selected={channel === "external"}
            onClick={() => setChannel("external")}
          >
            对外消息
            {Boolean(detail.readState?.unread.externalCount) && (
              <b className="case-tab-unread">
                {detail.readState?.unread.externalCount}
              </b>
            )}
          </button>
        )}
      </div>
      <div className="case-message-pane">
        <div
          className="case-messages"
          ref={messageList}
          onScroll={handleMessageScroll}
        >
          {(channel === "internal"
            ? historyState.internal.hasMore
            : historyState.external.hasMore) && (
            <button
              className="case-load-earlier"
              disabled={loadingEarlier}
              onClick={() => void loadEarlier()}
            >
              {loadingEarlier ? "加载中…" : "加载更早消息"}
            </button>
          )}
          {messages.map((m) => {
            return (
              <Fragment key={m.id}>
                {m.id === firstUnreadMessageId && (
                  <div className="case-unread-divider">以下为未读消息</div>
                )}
                <article
                  className={`case-message ${channel}${m.sentByCurrentUser ? " mine" : ""}${m.system ? " system" : ""}`}
                  data-message-id={m.id}
                  data-read-message-id={
                    m.readEligible ? m.readPosition?.messageId : undefined
                  }
                  data-read-at={m.readEligible ? m.readPosition?.at : undefined}
                >
                  {m.system ? (
                    <div className="case-system-message-content">
                      <span>{m.text}</span>
                      <time dateTime={m.time}>{dateLabel(m.time)}</time>
                    </div>
                  ) : (
                    <>
                      {m.authorName && (
                        <MessageAuthor
                          name={m.authorName}
                          avatarUrl={m.avatarUrl}
                        />
                      )}
                      <small className="case-message-header">
                        {channel === "external" && m.direction === "OUTBOUND"
                          ? messageAddressHeader(
                              m.sentByCurrentUser,
                              m.senderPresentation?.displayName,
                              m.senderPresentation?.mailboxEmail,
                              m.senderPresentation?.mailboxRoleType,
                              m.recipients,
                            )
                          : channel === "external" && m.sender?.email
                            ? `${m.sender.name?.trim() ? `${m.sender.name.trim()} · ` : ""}${m.sender.email} → ${
                                m.recipients
                                  ?.map((item) => item.email)
                                  .filter(Boolean)
                                  .join(", ") || "收件人未记录"
                              }`
                            : m.label}
                      </small>
                      <MessageBody
                        html={m.html}
                        text={m.text}
                        emphasizeQuotedHistory={channel === "external"}
                        transparentBackground={channel === "internal"}
                        onLayout={keepOpenedViewAtLatest}
                      />
                      {"fileAttachments" in m && <MailAttachmentList files={m.fileAttachments} separated />}
                      <div className="case-message-footer">
                        <time className="case-message-time" dateTime={m.time}>
                          {dateLabel(m.time)}
                        </time>
                        {channel === "external" &&
                          m.direction === "OUTBOUND" && (
                            <div className="case-message-send-status">
                              <span className="success">发送成功</span>
                            </div>
                          )}
                      </div>
                      {m.pending?.status === "failed" && (
                        <button
                          onClick={() => void sendInternal(m.pending!, false)}
                        >
                          重试发送
                        </button>
                      )}
                    </>
                  )}
                </article>
              </Fragment>
            );
          })}
          {!messages.length && (
            <p className="case-empty">
              暂无{channel === "internal" ? "对内" : "对外"}消息
            </p>
          )}
          {channel === "external" &&
            detail.drafts
              .filter(
                (d) =>
                  ["SENDING", "FAILED", "UNKNOWN"].includes(d.status) &&
                  !withdrawnDraftIds.has(d.id),
              )
              .map((d) => (
                <article
                  className={`case-message external mine case-draft${d.status === "FAILED" ? " failed" : ""}`}
                  key={d.id}
                >
                  <small className="case-message-header">
                    {messageAddressHeader(
                      true,
                      d.senderDisplayName,
                      d.senderMailboxEmail,
                      d.senderMailboxRoleType,
                      d.recipients,
                    )}
                  </small>
                  <strong>{d.subject}</strong>
                  <MessageBody
                    html={d.htmlBody}
                    text={d.textBody}
                    emphasizeQuotedHistory
                    onLayout={keepOpenedViewAtLatest}
                  />
                  <MailAttachmentList files={d.fileAttachments} separated />
                  <div className="case-message-send-status case-message-footer">
                    <time className="case-message-time" dateTime={d.createdAt}>
                      {dateLabel(d.createdAt)}
                    </time>
                    <span className={d.status.toLowerCase()}>
                      {draftStatusNames[d.status] ?? d.status}
                    </span>
                    {d.status === "FAILED" && (
                      <>
                        <span
                          className="case-send-status-icon failure-reason"
                          role="img"
                          aria-label={`发送失败原因：${draftFailureReason(d)}`}
                          title={draftFailureReason(d)}
                        >
                          <IconAlertCircle size={15} aria-hidden="true" />
                        </span>
                        <span title="重新发送">
                          <button
                            type="button"
                            className="case-send-status-icon retry"
                            aria-label="重新发送"
                            disabled={
                              sending ||
                              busy ||
                              !detail.permissions.externalSend ||
                              !canRetryDefiniteSendFailure(d)
                            }
                            onClick={() => {
                              if (
                                !window.confirm(
                                  `确认重新发送“${d.subject}”？系统已确认上次发送未被 Gmail 接收。`,
                                )
                              )
                                return;
                              void retrySend(d.id, d.latestSendId);
                            }}
                          >
                            <IconRefresh size={15} aria-hidden="true" />
                          </button>
                        </span>
                        <span title="撤回失败消息">
                          <button
                            type="button"
                            className="case-send-status-icon withdraw"
                            aria-label="撤回失败消息"
                            disabled={
                              sending ||
                              busy ||
                              !detail.permissions.externalSend ||
                              !isDefiniteSendFailure(d)
                            }
                            onClick={() =>
                              void withdrawFailedSend(d.id, d.subject)
                            }
                          >
                            <IconTrash size={15} aria-hidden="true" />
                          </button>
                        </span>
                      </>
                    )}
                  </div>
                </article>
              ))}
        </div>
        {!atBottom && currentUnreadCount > 0 && (
          <button
            type="button"
            className="case-new-messages"
            aria-live="polite"
            onClick={scrollToLatest}
          >
            {currentUnreadCount > 99 ? "99+" : currentUnreadCount} 条新消息 ↓
          </button>
        )}
      </div>
      <div className={`case-composer${noSenderMailbox ? " is-mailbox-unbound" : ""}`} aria-disabled={noSenderMailbox || undefined}>
        {channel === "external" && (
          <>
            {noSenderMailbox && (
              <p className="case-mailbox-unbound-notice" role="alert">
                当前账号未绑定任何发件邮箱
              </p>
            )}
            {detail.replyContext?.senders.length ? (
              <div
                className="case-sender-switch"
                role="group"
                aria-label="选择发件箱"
              >
                {detail.replyContext.senders.map((sender) => (
                  <button
                    key={sender.mailboxId}
                    type="button"
                    className={
                      sender.mailboxId === selectedMailboxId ? "active" : ""
                    }
                    aria-pressed={sender.mailboxId === selectedMailboxId}
                    disabled={composerDisabled || !sender.canSend}
                    title={sender.unavailableReason ?? undefined}
                    onClick={() => setSelectedMailboxId(sender.mailboxId)}
                  >
                    以{mailboxRoleName(sender.mailboxRoleType)}发件箱：
                    {sender.mailboxEmail}
                  </button>
                ))}
              </div>
            ) : null}
            <div className="case-reply-target">
              <span>回复最近客户来信</span>
              <strong>
                {detail.replyContext?.replyTarget.subject ?? "暂无可回复邮件"}
              </strong>
              {detail.replyContext?.replyTarget.receivedAt && (
                <small>
                  {dateLabel(detail.replyContext.replyTarget.receivedAt)}
                </small>
              )}
            </div>
            <div className="case-bcc-field">
              <label htmlFor={`case-bcc-${detail.id}`}>密送</label>
              <input
                id={`case-bcc-${detail.id}`}
                type="email"
                maxLength={320}
                value={bccEmail}
                disabled={composerDisabled}
                readOnly={!bccEditing}
                aria-readonly={!bccEditing}
                placeholder="不密送"
                onChange={(event) => setBccEmail(event.target.value)}
              />
              <button
                type="button"
                disabled={composerDisabled}
                onClick={() => setBccEditing((value) => !value)}
              >
                {bccEditing ? "完成" : "编辑"}
              </button>
            </div>
            <label className="case-signature-toggle">
              <input
                type="checkbox"
                checked={signatureEnabled}
                disabled={
                  busy ||
                  sending ||
                  !allowed ||
                  noSenderMailbox ||
                  !detail.replyContext?.signature?.configured
                }
                onChange={(event) => toggleSignature(event.target.checked)}
              />
              <span>启用个性签名</span>
              {!detail.replyContext?.signature?.configured && (
                <small>请先在个人设置中配置</small>
              )}
            </label>
          </>
        )}
        <CaseRichTextEditor
          ref={editor}
          ariaLabel={
            channel === "internal" ? "对内富文本消息" : "对外富文本消息"
          }
          disabled={composerDisabled}
          placeholder={
            channel === "internal" ? "输入对内消息，@ 选择用户" : "输入邮件正文"
          }
          initialHtml={
            channel === "external" && signatureEnabled && configuredSignature
              ? withSignature("", configuredSignature)
              : ""
          }
          mentionUsers={channel === "internal" ? mentionUsers : undefined}
          mentionStatus={mentionStatus}
          onRetryMentions={() => setMentionRetry((value) => value + 1)}
          onMentionsOpen={() => setMentionRetry((value) => value + 1)}
          resetKey={`${detail.id}-${channel}-${editorVersion}`}
          onChange={(snapshot) =>
            setEditorCharacterCount([
              ...messageTextWithoutSignature(snapshot.html),
            ].length)
          }
          onUploadImage={(file) => uploadCaseRichTextImage(detail.id, file)}
          onUploadStateChange={setEditorUploading}
        />
        {!allowed && <small>您没有此消息操作权限</small>}
        {channel === "internal" ? (
          <button
            className="case-primary"
            disabled={busy || editorUploading || !allowed}
            onClick={() => void submit()}
          >
            发布对内消息
          </button>
        ) : (
          <div className="case-email-actions">
            <label>
              <span className="sr-only">使用模板</span>
              <select
                aria-label="使用模板"
                value={selectedTemplateId}
                disabled={composerDisabled || editorUploading}
                onChange={(event) => useTemplate(event.target.value)}
              >
                <option value="">使用模板</option>
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              disabled={composerDisabled || editorUploading}
              onClick={() => void saveTemplate()}
            >
              保存为模板
            </button>
            {selectedTemplateId && (
              <button
                type="button"
                className="case-link-danger"
                disabled={composerDisabled || editorUploading}
                onClick={() => void removeSelectedTemplate()}
              >
                删除模板
              </button>
            )}
            <button
              className="case-primary"
              disabled={
                busy ||
                sending ||
                editorUploading ||
                !allowed ||
                noSenderMailbox ||
                !detail.replyContext?.canSend ||
                !detail.replyContext.recipient ||
                !selectedSender?.canSend ||
                !isOptionalEmail(bccEmail) ||
                attachmentsBlocked ||
                !externalBodyValid
              }
              onClick={() => void submit()}
              title={
                !externalBodyValid
                  ? "邮件正文至少需要 10 个字符"
                  : editorUploading
                    ? "请等待图片上传完成"
                    : (selectedSender?.unavailableReason ??
                      detail.replyContext?.unavailableReason ??
                      undefined)
              }
            >
              直接发送
            </button>
          </div>
        )}
        <div hidden={channel !== "external"}>
          <CaseMailAttachments key={`${detail.id}-${attachmentReset}`} caseId={detail.id}
            disabled={composerDisabled || Boolean(confirmingSend)}
            canReadQuote={Boolean(detail.permissions.quoteRead)}
            onChange={setMailAttachments} onBlockedChange={setAttachmentsBlocked} />
        </div>
        {channel === "external" && !noSenderMailbox && detail.replyContext?.unavailableReason && (
          <small className="case-send-unavailable">
            {detail.replyContext.unavailableReason}
          </small>
        )}
        {notice && <small role="status">{notice}</small>}
      </div>
      {confirmingSend && detail.replyContext && (
        <div className="case-send-modal-backdrop" role="presentation">
          <section
            className="case-send-modal"
            role="dialog"
            aria-modal={!confirmingSender}
            inert={confirmingSender}
            aria-labelledby="case-send-confirm-title"
          >
            <header>
              <h2 id="case-send-confirm-title">确认发送邮件</h2>
              <p>请确认发件身份、接收者和最终正文。</p>
            </header>
            <main>
              <dl className="case-send-summary">
                <div>
                  <dt>发送者</dt>
                  <dd>
                    <SenderIdentity sender={confirmingSend.sender} />
                  </dd>
                </div>
                <div>
                  <dt>接收者</dt>
                  <dd>
                    {detail.replyContext.recipient?.name
                      ? `${detail.replyContext.recipient.name} <${detail.replyContext.recipient.email}>`
                      : detail.replyContext.recipient?.email}
                  </dd>
                </div>
                {bccEmail.trim() ? (
                  <div>
                    <dt>密送</dt>
                    <dd>{bccEmail.trim()}</dd>
                  </div>
                ) : null}
                <div>
                  <dt>回复邮件</dt>
                  <dd>
                    {detail.replyContext.replyTarget.subject} ·{" "}
                    {dateLabel(detail.replyContext.replyTarget.receivedAt)}
                  </dd>
                </div>
                <div>
                  <dt>主题</dt>
                  <dd>{subject}</dd>
                </div>
              </dl>
              <MailAttachmentList files={mailAttachments} />
              <section className="case-send-preview" aria-label="邮件正文">
                <MessageBody
                  html={confirmingSend.html}
                  text={confirmingSend.text}
                />
              </section>
            </main>
            <footer>
              <button
                type="button"
                disabled={sending}
                onClick={() => setConfirmingSend(null)}
              >
                取消
              </button>
              <button
                type="button"
                className="case-primary"
                disabled={sending}
                onClick={() => void confirmDirectSend()}
              >
                {sending ? "正在提交…" : "确认发送"}
              </button>
            </footer>
          </section>
        </div>
      )}
      {confirmingSend && confirmingSender && (
        <SenderIdentityConfirmation sender={confirmingSend.sender}
          onCancel={() => setConfirmingSender(false)}
          onConfirm={() => void confirmDirectSend(true)} />
      )}
    </main>
  );
}
function CaseSidebar({
  detail,
  busy,
  mutate,
  onRefresh,
}: DetailProps & { onRefresh: () => void }) {
  const manualMetrics =
    detail.customMetrics ??
    manualOptions.map((key) => ({
      key,
      name: progressNames[key],
      enabled: true,
    }));
  const [selected, setSelected] = useState(detail.manualProgress),
    [updatingIndicator, setUpdatingIndicator] = useState(false),
    [evidence, setEvidence] = useState("");
  useEffect(() => {
    setSelected(detail.manualProgress);
  }, [detail.id, detail.rowVersion, detail.manualProgress]);
  const frozen = detail.businessOutcome === "FAILED",
    canProgress =
      detail.permissions.metricsManage &&
      !frozen &&
      !detail.progressReviewRequired &&
      detail.automaticProgress === "SALES_FOLLOW_UP";
  const automaticIndex = detail.progressReviewRequired
    ? -1
    : automaticOptions.indexOf(detail.automaticProgress ?? "");
  async function toggleIndicator(value: string, checked: boolean) {
    if (!canProgress || busy || updatingIndicator) return;
    if (
      !window.confirm(
        `确认${checked ? "勾选" : "取消"}“${manualMetrics.find((item) => item.key === value)?.name ?? value}”？\n案件：${detail.caseNumber}`,
      )
    )
      return;
    const next = checked
      ? [...selected.filter((item) => item !== value), value]
      : selected.filter((item) => item !== value);
    setUpdatingIndicator(true);
    try {
      if (await mutate({ action: "progress", selected: next }))
        setSelected(next);
    } finally {
      setUpdatingIndicator(false);
    }
  }
  return (
    <aside className="case-sidebar" aria-label="客户与案件指标">
      <section>
        <h2>客户 / 组织</h2>
        <strong>
          {detail.customer?.primaryContact?.displayName ?? "未关联客户"}
        </strong>
        <p>{detail.customer?.primaryContact?.normalizedEmail}</p>
        <p>
          {detail.customer?.customerOrganization?.displayName ?? "未关联组织"}
        </p>
      </section>
      {detail.permissions.metricsRead && (
        <section>
          <h2>案件指标</h2>
          {detail.progressReviewRequired && (
            <div className="case-review">
              <p>
                原状态：{detail.legacyStatus ?? "未知"} · 原进度：
                {detail.legacyProgress ?? "未知"}
              </p>
              <label>
                历史确认依据
                <textarea
                  value={evidence}
                  onChange={(e) => setEvidence(e.target.value)}
                  placeholder="填写核实依据；系统仅按已有提交证据恢复进度"
                />
              </label>
              <button
                disabled={
                  busy ||
                  !detail.permissions.metricsManage ||
                  frozen ||
                  !evidence.trim()
                }
                onClick={() =>
                  void mutate({
                    action: "confirm-history",
                    evidence,
                    reason: evidence,
                  })
                }
              >
                确认历史进度
              </button>
            </div>
          )}
          {frozen && (
            <p role="status">进度已冻结。重启该项目后，可继续修改。</p>
          )}
          <ul className="case-indicator-list" aria-label="案件指标任务列表">
            {automaticOptions.map((value, index) => (
              <li key={value} className="case-indicator-automatic">
                <label className="case-check" title="自动更新，无需手动操作">
                  <input
                    type="checkbox"
                    checked={automaticIndex >= index}
                    disabled
                  />
                  {progressNames[value]}
                  <small aria-hidden="true">自动</small>
                </label>
              </li>
            ))}
            {manualMetrics
              .filter((item) => item.enabled)
              .map(({ key: value, name }) => (
                <li
                  key={value}
                  className={
                    !canProgress ? "case-indicator-disabled" : undefined
                  }
                >
                  <label className="case-check">
                    <input
                      type="checkbox"
                      checked={selected.includes(value)}
                      disabled={!canProgress || busy || updatingIndicator}
                      onChange={(e) =>
                        void toggleIndicator(value, e.target.checked)
                      }
                    />
                    {name}
                  </label>
                </li>
              ))}
          </ul>
          {!canProgress &&
            !frozen &&
            !detail.progressReviewRequired &&
            detail.permissions.metricsManage && (
              <small>进入销售跟进后，可修改自定义指标。</small>
            )}
          {!detail.permissions.metricsManage && (
            <small>没有案件指标修改权限</small>
          )}
        </section>
      )}
      {detail.permissions.statusRead && (
        <CaseStatusSettings
          key={`status-${detail.id}`}
          detail={detail}
          busy={busy}
          mutate={mutate}
        />
      )}
      {(detail.permissions.tagApply || detail.permissions.tagManage) && (
        <CaseTags
          key={`tags-${detail.id}`}
          caseId={detail.id}
          canApply={detail.permissions.tagApply}
          canManage={detail.permissions.tagManage}
          onChange={onRefresh}
        />
      )}
      <Documents
        detail={detail}
        busy={busy}
        mutate={mutate}
        onRefresh={onRefresh}
      />
      <CaseOperationHistory detail={detail} />
      {detail.permissions.collaborationRead && (
        <CaseCollaborationPanel
          key={`collaboration-${detail.id}`}
          caseId={detail.id}
          busy={busy}
          canManage={detail.permissions.collaborationManage}
        />
      )}
    </aside>
  );
}
function CaseCollaborationPanel({
  caseId,
  busy,
  canManage,
}: {
  caseId: string;
  busy: boolean;
  canManage: boolean;
}) {
  const [data, setData] = useState<CaseCollaboration | null>(null),
    [loading, setLoading] = useState(true),
    [working, setWorking] = useState(false),
    [open, setOpen] = useState(false),
    [search, setSearch] = useState(""),
    [error, setError] = useState("");
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    void loadCaseCollaboration(caseId, controller.signal)
      .then(setData)
      .catch((cause) => {
        if (!controller.signal.aborted)
          setError(cause instanceof Error ? cause.message : "协作成员加载失败");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [caseId]);
  async function add(userId: string) {
    if (working || busy) return;
    setWorking(true);
    setError("");
    try {
      setData(await addCaseCollaborationMember(caseId, userId));
      setOpen(false);
      setSearch("");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "加入协作失败");
    } finally {
      setWorking(false);
    }
  }
  async function remove(userId: string, displayName: string) {
    if (
      working ||
      busy ||
      !window.confirm(`确认将“${displayName}”移出本案件协作？`)
    )
      return;
    setWorking(true);
    setError("");
    try {
      setData(await removeCaseCollaborationMember(caseId, userId));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "移除协作成员失败");
    } finally {
      setWorking(false);
    }
  }
  const normalized = search.trim().toLocaleLowerCase();
  const candidates = (data?.candidates ?? []).filter((candidate) =>
    [candidate.displayName, candidate.email, candidate.occupation]
      .filter(Boolean)
      .some((value) => value!.toLocaleLowerCase().includes(normalized)),
  );
  return (
    <section className="case-collaboration" aria-label="协作">
      <div className="case-collaboration-heading">
        <div>
          <h2>协作</h2>
          {data?.team && <small>{data.team.name}</small>}
        </div>
        {canManage && data?.canManage && (
          <button
            type="button"
            aria-expanded={open}
            disabled={busy || working}
            onClick={() => setOpen((value) => !value)}
          >
            添加成员
          </button>
        )}
      </div>
      {loading && <p>正在加载协作成员…</p>}
      {error && (
        <p className="case-collaboration-error" role="alert">
          {error}
        </p>
      )}
      {open && (
        <div className="case-collaboration-picker">
          <div className="case-collaboration-picker-heading">
            <strong>选择协作成员</strong>
            <button
              type="button"
              className="case-collaboration-picker-close"
              aria-label="关闭添加成员"
              onClick={() => {
                setOpen(false);
                setSearch("");
              }}
            >
              ×
            </button>
          </div>
          <input
            aria-label="搜索可加入的协作成员"
            autoFocus
            placeholder="搜索姓名、邮箱或职位"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <div className="case-collaboration-candidates">
            {candidates.map((candidate) => (
              <button
                type="button"
                key={candidate.userId}
                disabled={working || busy}
                onClick={() => void add(candidate.userId)}
              >
                <strong>{candidate.displayName}</strong>
                <small>
                  {candidate.email || candidate.occupation || "工作空间成员"}
                </small>
              </button>
            ))}
            {!candidates.length && <p>没有可加入的成员</p>}
          </div>
        </div>
      )}
      <div className="case-collaboration-members">
        {data?.members.map((member) => (
          <article key={member.userId} className="case-collaboration-member">
            <span className="case-collaboration-avatar" aria-hidden="true">
              {Array.from(member.displayName.trim())
                .slice(0, 2)
                .join("")
                .toLocaleUpperCase() || "?"}
            </span>
            <span className="case-collaboration-identity">
              <strong>{member.displayName}</strong>
              <small>
                {member.email || member.occupation || "未填写联系信息"}
              </small>
            </span>
            <span
              className={`case-collaboration-origin is-${member.origin.toLocaleLowerCase()}`}
            >
              {member.origin === "TEAM"
                ? "团队"
                : member.origin === "ASSIGNEE"
                  ? "分配"
                  : "加入"}
            </span>
            {canManage && member.removable && (
              <button
                type="button"
                className="case-collaboration-remove"
                aria-label={`移除协作成员 ${member.displayName}`}
                disabled={busy || working}
                onClick={() => void remove(member.userId, member.displayName)}
              >
                移除
              </button>
            )}
          </article>
        ))}
        {!loading && data?.members.length === 0 && <p>当前没有协作成员</p>}
      </div>
      <small className="case-collaboration-note">
        团队或原分配成员由系统维护；手动加入仅对本案件生效。
      </small>
    </section>
  );
}
function Documents({
  detail,
  busy,
  mutate,
  onRefresh,
}: DetailProps & { onRefresh: () => void }) {
  const [form, setForm] = useState(""),
    [editFinance, setEditFinance] = useState<
      CaseDetail["finance"][number] | null
    >(null);
  async function submit(e: FormEvent<HTMLFormElement>, action: string) {
    e.preventDefault();
    const values = Object.fromEntries(new FormData(e.currentTarget));
    if (
      await mutate({
        ...values,
        action,
        ...(action === "finance" && editFinance
          ? { documentId: editFinance.id, documentVersion: editFinance.version }
          : {}),
      })
    ) {
      setForm("");
      setEditFinance(null);
    }
  }
  return (
    <>
      {detail.permissions.sourcingRead && (
        <CaseSourcingPanel
          key={`sourcing-${detail.id}`}
          caseId={detail.id}
          disabled={busy || detail.businessOutcome === "FAILED"}
          canManage={detail.permissions.sourcingManage}
          onChanged={onRefresh}
        />
      )}
      {detail.permissions.quoteRead && (
        <CaseQuotePanel
          caseId={detail.id}
          disabled={busy || detail.businessOutcome === "FAILED"}
          canManage={detail.permissions.quoteManage}
          onChanged={onRefresh}
        />
      )}
      <section>
        <h2>财务单</h2>
        {detail.finance.map((f) => (
          <article className="case-document" key={f.id}>
            <strong>财务单 v{f.version}</strong>
            <p>{f.paymentInstructions}</p>
            <button
              disabled={busy || !detail.permissions.finance}
              onClick={() => {
                setEditFinance(f);
                setForm("finance");
              }}
            >
              编辑付款说明 / 报价来源
            </button>
            <details>
              <summary>报价快照</summary>
              <pre>{JSON.stringify(f.quoteSnapshot, null, 2)}</pre>
            </details>
          </article>
        ))}
        <button
          disabled={busy || !detail.permissions.finance}
          onClick={() => {
            setEditFinance(null);
            setForm(form === "finance" ? "" : "finance");
          }}
        >
          新建财务单
        </button>
        {form === "finance" && (
          <form
            key={editFinance?.id ?? "new"}
            onSubmit={(e) => void submit(e, "finance")}
          >
            <label>
              报价来源
              <select
                name="quoteVersionId"
                required
                defaultValue={editFinance?.quoteVersionId ?? ""}
              >
                <option value="">选择已提交报价</option>
                {detail.quotes
                  .filter((q) => q.submittedAt)
                  .map((q) => (
                    <option value={q.id} key={q.id}>
                      v{q.versionNumber} {q.currency} {q.total ?? q.subtotal}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              付款说明
              <textarea
                name="paymentInstructions"
                required
                defaultValue={editFinance?.paymentInstructions ?? ""}
              />
            </label>
            <button disabled={busy}>保存财务单</button>
          </form>
        )}
      </section>
    </>
  );
}
